// Client-side session expiry. The server rejects an expired sign-in token, but
// the browser only found out on its next request — so a screen left open
// overnight stayed readable, and clicking flashed cached data before the 401
// bounced to /login. These helpers let the app lock itself the moment the
// token's `exp` passes (including on wake from sleep).
import { clearShiftCache } from "@/lib/offline";

// Pages that work without a session — expiry/401 handling must never bounce the
// user off these (it would abandon a password-reset or signing link).
export const PUBLIC_PATHS = ["/login", "/reset", "/sign/", "/support-sign-in"];

export const onPublicPage = () =>
  PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));

// Fired on window when the session ends, so AuthContext can blank the screen
// before the reload to /login finishes.
export const SESSION_ENDED = "prydes:session-ended";

// Expiry of a JWT in epoch ms, or null if it can't be read (the server still
// rejects it, so an unreadable token just falls back to the 401 path).
export function tokenExpiry(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const exp = JSON.parse(atob(part.padEnd(Math.ceil(part.length / 4) * 4, "="))).exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token, now = Date.now()) {
  const exp = token ? tokenExpiry(token) : null;
  return exp !== null && now >= exp;
}

let ending = false;

// Sign out everywhere in this tab: drop the token, tell the UI to unmount
// signed-in pages, wipe the offline shift cache, then reload onto /login (a
// full load also discards every in-memory query cache).
export async function endSession(reason) {
  if (ending) return;
  ending = true;
  const token = localStorage.getItem("token");
  // Signing out (not expiry): tell the server, so a copy of this token stops
  // working too. keepalive lets the request finish during the reload.
  if (!reason && token && !isTokenExpired(token)) {
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/auth/logout`, {
      method: "POST", headers: { Authorization: `Bearer ${token}` }, keepalive: true,
    }).catch(() => {});
  }
  localStorage.removeItem("token");
  window.dispatchEvent(new Event(SESSION_ENDED));
  await Promise.race([clearShiftCache().catch(() => {}), new Promise((r) => setTimeout(r, 1000))]);
  window.location.replace(reason === "expired" ? "/login?expired=1" : "/login");
}
